/**
 * Configuração do banco IndexedDB para armazenamento offline de levantamentos
 * 
 * Usa IndexedDB nativo para evitar dependência extra.
 * Se Dexie for necessário no futuro, pode ser adicionado facilmente.
 */

const DB_NAME = 'SurveyOfflineDB';
const DB_VERSION = 1;

let dbInstance = null;

/**
 * Abre ou cria o banco de dados IndexedDB
 * @returns {Promise<IDBDatabase>}
 */
export const openDatabase = () => {
  return new Promise((resolve, reject) => {
    if (dbInstance) {
      resolve(dbInstance);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      reject(new Error(`Erro ao abrir banco de dados: ${request.error}`));
    };

    request.onsuccess = () => {
      dbInstance = request.result;
      resolve(dbInstance);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      // Store de levantamentos
      if (!db.objectStoreNames.contains('surveys')) {
        const surveyStore = db.createObjectStore('surveys', { keyPath: 'id', autoIncrement: false });
        surveyStore.createIndex('localId', 'localId', { unique: true });
        surveyStore.createIndex('serverId', 'serverId', { unique: false });
        surveyStore.createIndex('clientName', 'clientName', { unique: false });
        surveyStore.createIndex('syncStatus', 'syncStatus', { unique: false });
        surveyStore.createIndex('createdAt', 'createdAt', { unique: false });
      }

      // Store de ambientes
      if (!db.objectStoreNames.contains('environments')) {
        const envStore = db.createObjectStore('environments', { keyPath: 'id', autoIncrement: false });
        envStore.createIndex('localId', 'localId', { unique: true });
        envStore.createIndex('serverId', 'serverId', { unique: false });
        envStore.createIndex('surveyId', 'surveyId', { unique: false });
        envStore.createIndex('localSurveyId', 'localSurveyId', { unique: false });
        envStore.createIndex('syncStatus', 'syncStatus', { unique: false });
      }

      // Store de itens de ambiente
      if (!db.objectStoreNames.contains('items')) {
        const itemStore = db.createObjectStore('items', { keyPath: 'id', autoIncrement: false });
        itemStore.createIndex('localId', 'localId', { unique: true });
        itemStore.createIndex('serverId', 'serverId', { unique: false });
        itemStore.createIndex('environmentId', 'environmentId', { unique: false });
        itemStore.createIndex('localEnvironmentId', 'localEnvironmentId', { unique: false });
        itemStore.createIndex('syncStatus', 'syncStatus', { unique: false });
      }

      // Store de arquivos (metadados)
      if (!db.objectStoreNames.contains('files')) {
        const fileStore = db.createObjectStore('files', { keyPath: 'id', autoIncrement: false });
        fileStore.createIndex('localId', 'localId', { unique: true });
        fileStore.createIndex('serverId', 'serverId', { unique: false });
        fileStore.createIndex('surveyId', 'surveyId', { unique: false });
        fileStore.createIndex('localSurveyId', 'localSurveyId', { unique: false });
        fileStore.createIndex('syncStatus', 'syncStatus', { unique: false });
      }

      // Store de fila de sincronização
      if (!db.objectStoreNames.contains('syncQueue')) {
        const syncStore = db.createObjectStore('syncQueue', { keyPath: 'id', autoIncrement: true });
        syncStore.createIndex('entity', 'entity', { unique: false });
        syncStore.createIndex('entityId', 'entityId', { unique: false });
        syncStore.createIndex('action', 'action', { unique: false });
        syncStore.createIndex('timestamp', 'timestamp', { unique: false });
        syncStore.createIndex('status', 'status', { unique: false });
      }
    };
  });
};

/**
 * Fecha a conexão com o banco de dados
 */
export const closeDatabase = () => {
  if (dbInstance) {
    dbInstance.close();
    dbInstance = null;
  }
};

/**
 * Limpa todos os dados do banco (para debugging)
 */
export const clearDatabase = async () => {
  const db = await openDatabase();
  const stores = ['surveys', 'environments', 'items', 'files', 'syncQueue'];
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(stores, 'readwrite');
    let completed = 0;
    
    stores.forEach(storeName => {
      const store = transaction.objectStore(storeName);
      const request = store.clear();
      
      request.onsuccess = () => {
        completed++;
        if (completed === stores.length) {
          resolve();
        }
      };
      
      request.onerror = () => {
        reject(new Error(`Erro ao limpar store ${storeName}`));
      };
    });
  });
};

export default { openDatabase, closeDatabase, clearDatabase };
